import { useQuery } from "@tanstack/react-query";

import { MDS_COUNTRY_ISD_URL } from "constants/config";
import { DAY } from "constants/queryCacheTimes";

const trimString = (value) => (value == null ? "" : String(value).trim());

const getCountryDetails = (item) =>
  item && typeof item.details === "object" ? item.details : null;

const getPrimaryIsdCode = (details) =>
  trimString(details?.isd_codes?.[0]?.code);

const getPrimaryCurrency = (details) => {
  const currency = details?.currencies?.[0];
  if (!currency || currency.id == null) {
    return null;
  }
  return currency;
};

export const mapCountryMappingsToIsdList = (response) => {
  if (!Array.isArray(response?.items) || response.items.length === 0) {
    return [];
  }

  return response.items
    .map((item) => {
      if (item?.id == null) {
        return null;
      }

      const details = getCountryDetails(item);
      if (!details) {
        return null;
      }

      const countryCode = trimString(details.code).toUpperCase();
      const displayName = trimString(details.display_name);
      const countryName = trimString(details.name).toUpperCase();
      const isdCode = getPrimaryIsdCode(details);

      if (!countryCode || !displayName || !countryName || !isdCode) {
        return null;
      }

      return {
        country_id: item.id,
        country_code: countryCode,
        isd_code: isdCode,
        display_name: displayName,
        country_name: countryName,
        value: isdCode,
        label: isdCode,
      };
    })
    .filter(Boolean);
};

export const getCountryIsdList = async () => {
  if (!MDS_COUNTRY_ISD_URL) {
    throw new Error("MDS country ISD URL is not configured");
  }

  const response = await fetch(MDS_COUNTRY_ISD_URL, {
    method: "POST",
    headers: {
      accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch country ISD list");
  }

  const data = await response.json();
  const countryIsdList = mapCountryMappingsToIsdList(data);

  if (!countryIsdList.length) {
    throw new Error("No country ISD mappings found");
  }

  return countryIsdList;
};

export const findCountryInIsdList = (countryIsdList, countryId) => {
  if (countryId == null) {
    return null;
  }
  return (
    countryIsdList.find(
      (country) => Number(country.country_id) === Number(countryId),
    ) || null
  );
};

export const findCountryByIsdCode = (countryIsdList, isdCode) => {
  if (!isdCode) {
    return null;
  }
  return (
    countryIsdList.find((country) => country.isd_code === isdCode) || null
  );
};

export const mapCountryMappingsToCurrencyByCountry = (response) => {
  if (!Array.isArray(response?.items) || response.items.length === 0) {
    return {};
  }

  return response.items.reduce((currencyByCountry, item) => {
    if (item?.id == null) {
      return currencyByCountry;
    }

    const currency = getPrimaryCurrency(getCountryDetails(item));
    if (currency?.id != null) {
      currencyByCountry[item.id] = currency.id;
    }
    return currencyByCountry;
  }, {});
};

export const getCurrencyByCountryMap = async () => {
  if (!MDS_COUNTRY_ISD_URL) {
    throw new Error("MDS country ISD URL is not configured");
  }

  const response = await fetch(MDS_COUNTRY_ISD_URL, {
    method: "POST",
    headers: {
      accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch currency by country map");
  }

  const data = await response.json();
  return mapCountryMappingsToCurrencyByCountry(data);
};

export const mapCountryMappingsToCurrencyList = (response) => {
  if (!Array.isArray(response?.items) || response.items.length === 0) {
    return [];
  }

  const currenciesByCode = new Map();

  response.items.forEach((item) => {
    const details = getCountryDetails(item);
    (details?.currencies || []).forEach((currency) => {
      const currencyCode = trimString(currency?.code).toUpperCase();
      if (!currencyCode || currenciesByCode.has(currencyCode)) {
        return;
      }

      currenciesByCode.set(currencyCode, {
        id: currency.id,
        name: trimString(currency.name) || currencyCode,
        code: currencyCode,
        display_name: trimString(currency.display_name) || currencyCode,
      });
    });
  });

  return Array.from(currenciesByCode.values());
};

export const getCurrencyList = async () => {
  if (!MDS_COUNTRY_ISD_URL) {
    throw new Error("MDS country ISD URL is not configured");
  }

  const response = await fetch(MDS_COUNTRY_ISD_URL, {
    method: "POST",
    headers: {
      accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch currency list");
  }

  const data = await response.json();
  return mapCountryMappingsToCurrencyList(data);
};

export const useCountryIsdList = () =>
  useQuery({
    queryKey: ["mds", "country-isd-list"],
    queryFn: getCountryIsdList,
    enabled: Boolean(MDS_COUNTRY_ISD_URL),
    staleTime: DAY,
  });

export const useCurrencyList = () =>
  useQuery({
    queryKey: ["mds", "currency-list"],
    queryFn: getCurrencyList,
    enabled: Boolean(MDS_COUNTRY_ISD_URL),
    staleTime: DAY,
  });

export const useCurrencyByCountryMap = () =>
  useQuery({
    queryKey: ["mds", "currency-by-country-map"],
    queryFn: getCurrencyByCountryMap,
    enabled: Boolean(MDS_COUNTRY_ISD_URL),
    staleTime: DAY,
  });
